/**
 * Referral Page Helper Functions
 */

import { REFERRAL_URL_BASE } from './constants';
import type { SharePlatform, ReferralStatus } from '../types';

/**
 * Generate full referral URL from code
 */
export function getReferralUrl(code: string): string {
  if (!code) return '';
  return `${REFERRAL_URL_BASE}/${code}`;
}

/**
 * Generate share URL for a specific platform
 */
export function getShareUrl(
  platform: SharePlatform,
  referralUrl: string,
  text: string,
  emailSubject?: string
): string {
  switch (platform) {
    case 'twitter':
      return `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(referralUrl)}`;
    case 'facebook':
      return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralUrl)}&quote=${encodeURIComponent(text)}`;
    case 'whatsapp':
      return `https://wa.me/?text=${encodeURIComponent(`${text} ${referralUrl}`)}`;
    case 'email':
      return `mailto:?subject=${encodeURIComponent(emailSubject || 'Check out Rumio!')}&body=${encodeURIComponent(`${text}\n\n${referralUrl}`)}`;
    default:
      return '';
  }
}

/**
 * Open share dialog in new window
 */
export function openShareWindow(url: string): void {
  if (url.startsWith('mailto:')) {
    window.location.href = url;
  } else {
    window.open(url, '_blank', 'width=600,height=400');
  }
}

/**
 * Copy text to clipboard
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    console.error('Failed to copy:', err);
    return false;
  }
}

/**
 * Get status badge styling
 */
export function getStatusBadgeStyle(status: ReferralStatus): {
  bg: string;
  text: string;
  icon: string;
} {
  switch (status) {
    case 'rewarded':
      return { bg: 'bg-blue-100', text: 'text-blue-700', icon: 'text-blue-600' };
    case 'qualified':
      return { bg: 'bg-green-100', text: 'text-green-700', icon: 'text-green-600' };
    case 'pending':
    default:
      return { bg: 'bg-amber-100', text: 'text-amber-700', icon: 'text-amber-600' };
  }
}

/**
 * Format date for display
 */
export function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Calculate rewards remaining this month
 */
export function calculateRewardsLeft(rewardsThisMonth: number, monthlyCap: number): number {
  return Math.max(0, monthlyCap - rewardsThisMonth);
}

/**
 * Format premium expiry countdown
 */
export function formatPremiumCountdown(daysRemaining: number): string {
  if (daysRemaining <= 0) return 'Expired';
  if (daysRemaining === 1) return '1 day remaining';
  return `${daysRemaining} days remaining`;
}

/**
 * Download blob as file
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Download SVG as PNG
 */
export async function downloadSvgAsPng(
  svgContent: string,
  filename: string,
  width: number,
  height: number
): Promise<void> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      reject(new Error('Could not get canvas context'));
      return;
    }

    canvas.width = width;
    canvas.height = height;

    const img = new Image();
    img.onload = () => {
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob((blob) => {
        if (blob) {
          downloadBlob(blob, filename);
          resolve();
        } else {
          reject(new Error('Failed to create blob'));
        }
      }, 'image/png');
    };

    img.onerror = () => reject(new Error('Failed to load SVG'));
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgContent)));
  });
}

/**
 * Download SVG directly
 */
export function downloadSvg(svgContent: string, filename: string): void {
  const blob = new Blob([svgContent], { type: 'image/svg+xml' });
  downloadBlob(blob, filename);
}
